import { ArrowDown, ArrowDownUp, ArrowUp, ChevronDown, SearchX, X } from 'lucide-react';
import { useReadQuery, useSuspenseQuery, type QueryRef } from '@apollo/client/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { Fragment, useMemo, useState, useTransition } from 'react';
import dayjs from 'dayjs';
import { debounce } from 'lodash';

import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import {
  AssetTypeEnum,
  AssetsOrderByEnum,
  DepositStatusEnum,
  EscrowPartyTypeEnum,
  SyncStatusEnum,
} from 'src/graphql/schema';
import { AssetsDocument } from 'src/graphql/actions/asset/assets.query';
import { withSuspense } from 'src/hoc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PROVIDER_KEY_TO_NAME } from 'src/shared/constants';
import { formatBytes } from 'src/utils/formatBytes';
import { Badge } from '@/components/ui/badge';
import { cn } from 'src/lib/utils';
import {
  AssetTypeIcon,
  AssetAlert,
  AssignAssetTypeDialog,
  AssetBlockSkeleton,
  depositStatusMap,
  depositStatusOptions,
  syncStatusMap,
  syncStatusOptions,
  toSyncStatusesFilter,
  refetchAssets,
} from 'src/containers/Deposits';
import { SimplePagination } from '@/components/ui/simple-pagination';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectLabel,
  SelectSeparator,
} from '@/components/ui/select';
import { InputSearch } from '@/components/ui/input-search';
import { EmptyBlock } from '@/components/ui/empty-block';

import { SuggestedChangesBlock } from './';

import type { IAssetsQuery } from 'src/graphql/actions/asset/assets.query';

type Props = {
  title: string;
  description: string;
  queryRef: QueryRef<IAssetsQuery>;
  ref: React.RefObject<HTMLDivElement | null>;
};

type IAssetRow = {
  id: string;
  asset: {
    depositSource: string;
    provider?: string;
    type: AssetTypeEnum;
  };
  size: string;
  date: {
    latestDeposit: string;
    lastUpdateCheck: string;
  };
  depositStatus: DepositStatusEnum;
  syncStatus?: SyncStatusEnum | null;
  alert: {
    syncStatus?: SyncStatusEnum | null;
    depositStatus: DepositStatusEnum;
    error?: string;
  };
  action: {
    id: string;
    escrowId: string;
    canAssign: boolean;
  };
};

const columnHelper = createColumnHelper<IAssetRow>();
const columns = [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => {
      const hasAssignableRows = table.getRowModel().rows.some((row) => row.original.action.canAssign);

      return (
        <Checkbox
          checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          disabled={!hasAssignableRows}
          aria-label="Select all"
        />
      );
    },
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        disabled={!row.original.action.canAssign}
      />
    ),
  }),
  columnHelper.accessor('asset', {
    header: (context) => `Assets (${context.table.getRowModel().rows.length})`,
    cell: (props) => {
      const { depositSource, provider, type } = props.getValue();

      const providerName = provider ? PROVIDER_KEY_TO_NAME[provider as keyof typeof PROVIDER_KEY_TO_NAME] : '';

      return (
        <div className="flex items-center gap-2">
          <AssetTypeIcon type={type} provider={provider} />
          <div className="grow min-w-0">
            <div className="truncate" title={depositSource}>
              {depositSource}
            </div>
            {providerName && <div className="text-xs text-muted-foreground">{providerName}</div>}
          </div>
        </div>
      );
    },
    sortingFn: (rowA, rowB) => {
      const valueA = rowA.original.asset.depositSource;
      const valueB = rowB.original.asset.depositSource;

      return valueA.localeCompare(valueB);
    },
    meta: {
      headerClassName: 'max-w-[352px]',
      cellClassName: 'max-w-[352px]',
    },
  }),
  columnHelper.accessor('size', {
    header: 'Size',
    cell: (props) =>
      props.getValue() ? <div className="whitespace-nowrap">{formatBytes(Number(props.getValue()))}</div> : null,
    enableSorting: false,
  }),
  columnHelper.accessor('date', {
    header: 'Last deposit',
    cell: (props) => {
      const { latestDeposit, lastUpdateCheck } = props.getValue();

      if (!latestDeposit) return null;

      const latestDepositDate = dayjs(latestDeposit).format('MMM DD, YYYY');
      const isLastCheckOverWeekAgo = lastUpdateCheck
        ? dayjs(lastUpdateCheck).isBefore(dayjs().subtract(1, 'week'))
        : false;
      const lastUpdateCheckDate = isLastCheckOverWeekAgo
        ? dayjs(latestDeposit).format('MMM DD, YYYY')
        : dayjs(lastUpdateCheck).fromNow();

      return (
        <Fragment>
          <div className="text-sm font-medium">{latestDepositDate}</div>
          {lastUpdateCheckDate && (
            <div className="text-[10px] text-muted-foreground whitespace-nowrap">Checked {lastUpdateCheckDate}</div>
          )}
        </Fragment>
      );
    },
    sortingFn: (rowA, rowB) => {
      const valueA = rowA.original.date.latestDeposit ? dayjs(rowA.original.date.latestDeposit).unix() : 0;
      const valueB = rowB.original.date.latestDeposit ? dayjs(rowB.original.date.latestDeposit).unix() : 0;

      return valueA - valueB;
    },
  }),
  columnHelper.accessor('syncStatus', {
    header: 'Sync status',
    cell: (props) => {
      const status = props.getValue();

      if (!status) return 'N/A';

      const syncStatus = syncStatusMap[status];

      if (!syncStatus) return 'N/A';

      const { label, textColor, dotColor } = syncStatus;

      return (
        <Badge variant="ghost" className={cn('gap-1 px-0', textColor)}>
          <span className={cn('rounded-full size-1.5', dotColor)} />
          {label}
        </Badge>
      );
    },
    sortingFn: 'text',
  }),
  columnHelper.accessor('depositStatus', {
    header: 'Deposit status',
    cell: (props) => {
      const { label, variant } = depositStatusMap[props.getValue()];

      return <Badge variant={variant}>{label}</Badge>;
    },
    sortingFn: 'text',
  }),
  columnHelper.accessor('alert', {
    header: '',
    cell: (props) => <AssetAlert {...props.getValue()} />,
    enableSorting: false,
    meta: {
      headerClassName: 'w-7',
      cellClassName: 'w-7',
    },
  }),
  columnHelper.accessor('action', {
    header: 'Asset type',
    cell: (props) => {
      return (
        <AssignAssetTypeDialog
          assetIds={[props.getValue().id]}
          refetch={refetchAssets}
          escrowId={props.getValue().escrowId}
        >
          <Button
            variant="outline"
            size="sm"
            disabled={
              props.table
                .getSelectedRowModel()
                .rows.map((row) => row.original.id)
                .includes(props.getValue().id) || !props.getValue().canAssign
            }
          >
            Label
          </Button>
        </AssignAssetTypeDialog>
      );
    },
    enableSorting: false,
    meta: {
      headerClassName: 'w-23.5',
      cellClassName: 'w-23.5',
    },
  }),
];

const DepositStatusSelect = ({
  depositStatus,
  setDepositStatus,
}: {
  depositStatus?: DepositStatusEnum;
  setDepositStatus: (value?: DepositStatusEnum) => void;
}) => {
  const onChange = (value: string) => setDepositStatus(value !== 'all' ? (value as DepositStatusEnum) : undefined);

  return (
    <Select onValueChange={onChange} value={depositStatus ?? 'all'}>
      <SelectTrigger className="w-10 sm:w-50 *:data-[slot=select-value]:hidden sm:*:data-[slot=select-value]:block">
        <SelectValue placeholder="Deposit status" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All</SelectItem>
        <SelectGroup>
          <SelectSeparator />
          <SelectLabel>Deposit status</SelectLabel>
          {depositStatusOptions.map(({ value, label }) => (
            <SelectItem value={value} key={value}>
              {label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

const SyncStatusSelect = ({
  syncStatus,
  setSyncStatus,
}: {
  syncStatus?: SyncStatusEnum;
  setSyncStatus: (value?: SyncStatusEnum) => void;
}) => {
  const onChange = (value: string) => setSyncStatus(value !== 'all' ? (value as SyncStatusEnum) : undefined);

  return (
    <Select onValueChange={onChange} value={syncStatus ?? 'all'}>
      <SelectTrigger className="w-10 sm:w-50 *:data-[slot=select-value]:hidden sm:*:data-[slot=select-value]:block">
        <SelectValue placeholder="Sync Status" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All</SelectItem>
        <SelectGroup>
          <SelectSeparator />
          <SelectLabel>Sync Status</SelectLabel>
          {syncStatusOptions.map(({ value, label }) => (
            <SelectItem value={value} key={value}>
              {label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

const AssetsActions = ({
  escrowId,
  assetIds,
  queryFilter,
  setPage,
  setQueryFilter,
  canAssign,
  depositStatus,
  setDepositStatus,
  syncStatus,
  setSyncStatus,
}: {
  escrowId: string;
  assetIds: string[];
  queryFilter: string;
  setPage: (value: number) => void;
  setQueryFilter: (value: string) => void;
  canAssign: boolean;
  depositStatus?: DepositStatusEnum;
  setDepositStatus: (value?: DepositStatusEnum) => void;
  syncStatus?: SyncStatusEnum;
  setSyncStatus: (value?: SyncStatusEnum) => void;
}) => {
  const [isPending, startTransition] = useTransition();

  const onChange = debounce(
    (e) =>
      startTransition(() => {
        setQueryFilter(e.target.value);
        setPage(1);
      }),
    500,
  );

  const onClearSearch = () => setQueryFilter('');

  const setInitialFilters = () => {
    setDepositStatus(undefined);
    setSyncStatus(undefined);
    setQueryFilter('');
  };

  return (
    <div>
      <div className="flex gap-2 justify-between">
        <div className="flex gap-2">
          <DepositStatusSelect depositStatus={depositStatus} setDepositStatus={setDepositStatus} />
          <SyncStatusSelect syncStatus={syncStatus} setSyncStatus={setSyncStatus} />
          <InputSearch
            isLoading={isPending}
            placeholder="Search"
            className="max-w-[280px] w-full"
            onChange={onChange}
            onClear={onClearSearch}
          />
        </div>
        <AssignAssetTypeDialog assetIds={assetIds} refetch={refetchAssets} escrowId={escrowId}>
          {assetIds.length && canAssign && <Button>Label selected</Button>}
        </AssignAssetTypeDialog>
      </div>
      {[depositStatus, syncStatus, queryFilter].some(Boolean) && (
        <Button variant="ghost" className="px-0" onClick={setInitialFilters}>
          <X /> Clear all
        </Button>
      )}
    </div>
  );
};

const AssetsLabelingBlock = ({ title, description, queryRef, ref }: Props) => {
  const [isOpen, setIsOpen] = useState(true);
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [queryFilter, setQueryFilter] = useState('');
  const [depositStatus, setDepositStatus] = useState<DepositStatusEnum | undefined>(undefined);
  const [syncStatus, setSyncStatus] = useState<SyncStatusEnum | undefined>();
  const [isPending, startTransition] = useTransition();
  const {
    softwareEscrow,
    softwareEscrow: { currentCompanyRole, policy },
  } = useSoftwareEscrow();
  const { data: dataAllAssets } = useReadQuery(queryRef);
  const { data: dataTakenAssets } = useSuspenseQuery(AssetsDocument, {
    variables: {
      depositStatus: DepositStatusEnum.Taken,
      softwareEscrowId: softwareEscrow.id,
      perPage: 1,
      page: 1,
      labeled: false,
      orderBy: AssetsOrderByEnum.LatestDeposit,
    },
    skip: !dataAllAssets?.assets.nodesCount,
  });
  const {
    data: { assets },
  } = useSuspenseQuery(AssetsDocument, {
    variables: {
      softwareEscrowId: softwareEscrow.id,
      perPage: 10,
      page,
      labeled: false,
      depositStatus,
      syncStatuses: toSyncStatusesFilter(syncStatus),
      ...(queryFilter && { queryFilter }),
    },
  });

  const tableData = useMemo(
    () =>
      assets.nodes.map((node) => ({
        id: node.id,
        asset: {
          depositSource: node.depositSource || '',
          provider: node.integration?.provider || '',
          type: node.type,
        },
        provider: node.integration?.provider || '',
        size: node.size || '',
        date: {
          latestDeposit: node.latestDeposit || '',
          lastUpdateCheck: node.lastUpdateCheck || '',
        },
        depositStatus: node.depositStatus || '',
        syncStatus: node.syncStatus,
        alert: {
          depositStatus: node.depositStatus,
          syncStatus: node.syncStatus,
          error: node.error ?? undefined,
        },
        action: {
          id: node.id,
          escrowId: softwareEscrow.id,
          canAssign: policy.depositAssets,
        },
      })),
    [assets.nodes, softwareEscrow.id],
  );
  const table = useReactTable({
    data: tableData,
    columns:
      currentCompanyRole === EscrowPartyTypeEnum.Beneficiary
        ? columns.filter((column) => 'accessorKey' in column && column.accessorKey !== 'action')
        : columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  const onPageChange = (page: number) => startTransition(() => setPage(page));
  const onPrevious = () => onPageChange(page - 1);
  const onNext = () => onPageChange(page + 1);

  if (!dataAllAssets.assets.nodesCount) return null;

  const infoItems = [
    {
      label: 'Total number of assets',
      value: dataAllAssets.assets.nodesCount,
    },
    {
      label: 'Last updated',
      value: dataTakenAssets?.assets.nodes[0]?.latestDeposit
        ? dayjs(dataTakenAssets.assets.nodes[0].latestDeposit).format('MMM DD, YYYY')
        : '-',
    },
  ];

  return (
    <section ref={ref}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen} asChild>
        <Card>
          <CardHeader className="flex-row justify-between items-center gap-2 space-y-0 [&:has(+[data-state=open])]:pb-0">
            <div className="text-base font-semibold">{title}</div>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="text-xs">
                {isOpen ? 'Hide' : 'Show'} assets
                <ChevronDown className={cn('transition-transform rotate-0', isOpen && 'rotate-180')} />
              </Button>
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent className="pb-0">
            <CardContent className="space-y-6">
              <CardDescription className="mb-6">{description}</CardDescription>
              <SuggestedChangesBlock />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {infoItems.map(({ label, value }) => (
                  <div key={label} className="bg-background-secondary border border-border rounded-lg p-5 space-y-1">
                    <div className="text-lg font-bold text-foreground">{value}</div>
                    <div className="text-xs text-muted-foreground">{label}</div>
                  </div>
                ))}
              </div>
              <AssetsActions
                escrowId={softwareEscrow.id}
                assetIds={table.getSelectedRowModel().rows.map((row) => row.original.id)}
                queryFilter={queryFilter}
                setPage={setPage}
                setQueryFilter={setQueryFilter}
                canAssign={policy.depositAssets}
                depositStatus={depositStatus}
                setDepositStatus={setDepositStatus}
                syncStatus={syncStatus}
                setSyncStatus={setSyncStatus}
              />
              <div className="border border-border rounded-md">
                <Table isLoading={isPending}>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead
                            key={header.id}
                            className={cn(
                              'bg-gray-50 text-xs text-muted-foreground font-bold whitespace-nowrap first:rounded-tl-md last:rounded-tr-md',
                              header.column.columnDef.meta?.headerClassName,
                            )}
                          >
                            {header.column.getCanSort() ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={header.column.getToggleSortingHandler()}
                                className="-ml-3 text-muted-foreground! text-xs! font-bold"
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                {{
                                  asc: <ArrowUp />,
                                  desc: <ArrowDown />,
                                }[header.column.getIsSorted() as string] ?? <ArrowDownUp />}
                              </Button>
                            ) : (
                              flexRender(header.column.columnDef.header, header.getContext())
                            )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows.length ? (
                      table.getRowModel().rows.map((row) => (
                        <TableRow key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={table.getAllColumns().length} className="h-4 text-center">
                          <EmptyBlock
                            title="No results"
                            icon={SearchX}
                            withCard={false}
                            classes={{
                              title: 'text-sm',
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <SimplePagination
                page={page}
                totalPages={assets.pagesCount}
                totalItems={assets.nodesCount}
                limit={10}
                onPrevious={onPrevious}
                onNext={onNext}
                isPreviousDisabled={page <= 1}
                isNextDisabled={page >= assets.pagesCount}
                hideOnSinglePage
                className="justify-end"
              />
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </section>
  );
};

export default withSuspense(AssetsLabelingBlock, AssetBlockSkeleton);
